import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, ShieldCheck, MessageCircle, Lock, ChevronDown, ChevronUp, Star, Download, X, User, Mail, Phone, Utensils, HelpCircle, Play } from 'lucide-react';

const App = () => {
  // ==================================================================================
  // ⚙️ ÁREA DE CONFIGURAÇÃO (MASTER)
  // ==================================================================================
  
  // Link do Checkout (Com Widget Hotmart ativado)
  const CHECKOUT_LINK = "https://pay.hotmart.com/Q103046465Y?checkoutMode=10"; 
  
  // VSL (Vídeo de Vendas Principal)
  const VSL_EMBED_URL = "https://www.youtube.com/embed/ly4HQ-Isb_8?controls=0&rel=0";
  
  // LINK DO PDF DE BÔNUS (O arquivo deve estar na mesma pasta na Hostinger)
  const LINK_DO_PDF_BONUS = "https://hb.twovortex.com/guia-receitas.pdf"; 

  // Integração com o arquivo PHP
  const API_URL = "./leads.php";

  // LINKS DOS VÍDEOS DEPOIMENTOS (IDs do YouTube - shorts)
  const VIDEO_GABRIELLA = "-s1I_KbbzwM";
  const VIDEO_JULIA = "GbThLQt-3f4";
  const VIDEO_CAMILA = "Lx-zbCwjC_I";
  const TESTIMONIALS = [
    { videoId: VIDEO_GABRIELLA, nome: "Gabriella, 27 anos - Advogada", resultado: "Acabou com a halitose do marido" },
    { videoId: VIDEO_JULIA, nome: "Julia, 21 anos - Personal", resultado: "Tinha halitose por causa da dieta" },
    { videoId: VIDEO_CAMILA, nome: "Camila, 30 anos - Dona de Restaurante", resultado: "Voltou a atender clientes sorrindo" },
  ];

  // ==================================================================================

  const [faqOpen, setFaqOpen] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [timeLeft, setTimeLeft] = useState(20 * 60);
  
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);

  // 1. Script Hotmart (Widget)
  useEffect(() => {
    if (document.getElementById('hotmart-lib')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://static.hotmart.com/css/hotmart-fb.min.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://static.hotmart.com/checkout/widget.min.js';
    script.async = true;
    script.id = 'hotmart-lib';
    document.head.appendChild(script);
  }, []);

  // 2. Exit Intent (Detector de Saída)
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasTriggered) {
        setModalOpen(true);
        setHasTriggered(true);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasTriggered]);

  // 3. CronÇ´metro de urgÇ½ncia
  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  const toggleFaq = (index) => {
    setFaqOpen(faqOpen === index ? null : index);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 1. Envia para o PHP (Salva o Lead e Dispara Email)
      await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      // 2. Feedback e Download Forçado (Técnica Anti-Bloqueio)
      setTimeout(() => {
        setLoading(false);
        setModalOpen(false);
        setFormData({ name: '', email: '', phone: '' });
        
        // Cria um link invisível e clica nele (Melhor que window.open)
        const link = document.createElement('a');
        link.href = LINK_DO_PDF_BONUS;
        link.target = '_blank';
        link.setAttribute('download', 'O-Jantar-do-Beijo.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        alert("Sucesso! O livro foi enviado para o seu e-mail e o download começou.");
      }, 1500);

    } catch (error) {
      console.error("Erro:", error);
      setLoading(false);
      // Fallback se der erro no PHP
      window.location.href = LINK_DO_PDF_BONUS; 
    }
  };

  const formatTime = (seconds) => {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${minutes}:${secs}`;
  };

  const VideoReel = ({ videoId, nome, resultado }) => {
    const thumb = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;
    return (
      <a
        href={watchUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block bg-slate-800 rounded-2xl overflow-hidden shadow-xl border border-slate-700 hover:border-orange-400/60 transition-colors"
      >
        <div className="relative w-full h-[190px] md:h-[220px] bg-black">
          <img src={thumb} alt={`Depoimento de ${nome}`} className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-14 w-14 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-lg">
              <Play size={26} fill="currentColor" />
            </div>
          </div>
        </div>
        <div className="p-5 space-y-2">
          <div className="flex text-orange-400 gap-1">{[1,2,3,4,5].map(s => <Star key={s} size={12} fill="currentColor" />)}</div>
          <p className="text-white font-bold text-base leading-tight">"{resultado}"</p>
          <p className="text-slate-400 text-sm font-medium">{nome}</p>
        </div>
      </a>
    );
  };

  return (
    <div className="font-sans text-slate-900 bg-white relative">
      
      {/* MODAL DE CAPTURA */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl max-w-lg w-full p-0 relative shadow-2xl overflow-hidden border border-white/20">
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white text-center py-3 font-bold uppercase tracking-wider text-sm shadow-md">
              Espere! Não vá de mãos vazias 🎁
            </div>
            <button onClick={() => setModalOpen(false)} className="absolute top-3 right-3 text-white/80 hover:text-white bg-black/20 hover:bg-black/40 rounded-full p-1 transition-colors z-10"><X size={20} /></button>
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-sm">
                  <Utensils className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Livro de Receitas Grátis</h3>
                <p className="text-slate-600 text-sm leading-relaxed">Baixe o <strong>"O Jantar do Beijo"</strong>. 7 Receitas Pré-Encontro que blindam o hálito e garantem zero fermentação estomacal.</p>
              </div>
              <form className="space-y-4" onSubmit={handleFormSubmit}>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 text-slate-400" size={18} />
                  <input type="text" name="name" required placeholder="Seu primeiro nome" className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" value={formData.name} onChange={handleInputChange} />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-3.5 text-slate-400" size={18} />
                  <input type="tel" name="phone" required placeholder="Seu WhatsApp (com DDD)" className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" value={formData.phone} onChange={handleInputChange} />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 text-slate-400" size={18} />
                  <input type="email" name="email" required placeholder="Seu melhor e-mail" className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" value={formData.email} onChange={handleInputChange} />
                </div>
                <button disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-lg shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70">
                  {loading ? 'Enviando...' : (<><Download size={20} /> QUERO O LIVRO DE RECEITAS</>)}
                </button>
              </form>
              <p className="text-[10px] text-center text-slate-400 mt-4">Seus dados estão 100% seguros.</p>
            </div>
          </div>
        </div>
      )}

      {/* BARRA SUPERIOR */}
      <div className="bg-blue-600 text-white text-xs md:text-sm py-2 text-center font-bold cursor-pointer hover:bg-blue-700 transition-colors" onClick={() => setModalOpen(true)}>
        🍽️ Presente para você: Clique aqui para baixar o Livro "O Jantar do Beijo" Grátis.
      </div>

      {/* HERO SECTION */}
      <header className="bg-slate-900 text-white pt-8 pb-16 px-4 md:px-8 text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="bg-orange-600 text-xs font-bold px-4 py-1 rounded-full inline-block mb-6 uppercase tracking-wider shadow-lg border border-orange-500">
            Revelação Científica 2025
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight mb-6 tracking-tight">
            O Protocolo Oculto de 15 Dias que <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">Elimina o Mau Hálito</span> Pela Raiz
          </h1>
          <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 font-light">
            Sem cirurgias, sem pastilhas. Reative a defesa natural da sua saliva.
          </p>
          <div className="relative w-full max-w-4xl mx-auto rounded-xl shadow-2xl border border-slate-700 bg-black overflow-hidden aspect-video">
            <iframe src={VSL_EMBED_URL} className="w-full h-full absolute inset-0" frameBorder="0" allowFullScreen title="Vídeo de Vendas"></iframe>
          </div>
          <div className="animate-bounce mt-12">
            <a
              href={CHECKOUT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white text-xl md:text-2xl font-black py-6 px-8 rounded-lg shadow-xl shadow-green-500/20 transition-all transform hover:-translate-y-1 flex flex-col items-center justify-center gap-1 mx-auto max-w-md w-full decoration-0 border-b-4 border-green-800"
            >
              <span>QUERO BLINDAR MEU HÁLITO</span>
              <span className="text-xs font-normal opacity-90 flex items-center gap-1"><Lock size={12} /> Compra 100% Segura</span>
            </a>
          </div>
        </div>
      </header>

      {/* DOR */}
      <section className="py-16 px-4 bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-slate-800">Situações que acabam hoje:</h2>
          <div className="grid gap-4">
            {["Pessoas oferecendo chiclete.", "Alguém dando um passo para trás.", "Gosto amargo ao acordar.", "Vergonha de falar perto."].map((item, i) => (
              <div key={i} className="flex items-start bg-white p-5 rounded-xl shadow-sm border border-slate-100">
                <AlertTriangle className="text-red-500 shrink-0 mt-1 mr-4" size={20} />
                <p className="text-slate-700 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUÇÃO */}
      <section className="py-20 px-4 bg-white text-center">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-16">Como Funciona?</h2>
          <div className="grid md:grid-cols-3 gap-8 text-left">
            {[
              {icon: ShieldCheck, title: "Fim da Saburra", text: "Remova a camada branca da língua sem ânsia."},
              {icon: CheckCircle, title: "Reset Intestinal", text: "Pare a fermentação no estômago."},
              {icon: MessageCircle, title: "Saliva Protetora", text: "Estimule a produção natural de saliva alcalina."}
            ].map((item, i) => (
              <div key={i} className="p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 transition-all">
                <item.icon className="text-blue-600 mb-4" size={40} />
                <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REELS */}
      <section className="py-20 bg-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Veja com seus próprios olhos</h2>
          <p className="text-slate-400 text-center mb-12">Histórias reais de alunos que recuperaram a confiança.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((item) => (
              <VideoReel key={item.videoId} {...item} />
            ))}
          </div>
        </div>
      </section>

      {/* OFERTA STACK */}
      <section className="py-20 px-4 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-900 p-8 text-center text-white space-y-3">
            <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-red-200 font-semibold">
              <span className="h-2 w-2 rounded-full bg-red-400 animate-pulse"></span>
              Oferta por tempo limitado
            </div>
            <div className="text-4xl md:text-5xl font-black">{formatTime(timeLeft)}</div>
            <p className="text-slate-400 text-xs">O desconto trava quando o relógio zerar.</p>
          </div>
          <div className="p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              {[
                { label: "Método Hálito Blindado", price: "R$ 717,90", free: false, highlight: false },
                { label: "Bônus 1: Guia Anti-Fermentação", price: "R$ 59,90", free: true, highlight: false },
                { label: "Bônus 2: O Jantar do Beijo", price: "R$ 67,00", free: true, highlight: false },
                { label: "Bônus 3: Manual Fale de Perto", price: "R$ 97,00", free: true, highlight: false },
                { label: "Bônus 4: Grupo VIP de Apoio", price: "R$ 197,00", free: true, highlight: true },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`flex justify-between items-center p-3 rounded-lg border ${
                    item.highlight ? "bg-blue-50 border-blue-100" : "bg-slate-50 border-slate-100"
                  }`}
                >
                  <div className="flex gap-3 items-center">
                    <CheckCircle className="text-green-500 shrink-0" size={20} />
                    <span className={`text-sm ${item.highlight ? "font-bold text-blue-900" : "font-medium text-slate-800"}`}>
                      {item.label}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className={`block text-[10px] text-red-400 line-through ${item.free ? "" : "font-semibold"}`}>
                      {item.price}
                    </span>
                    {item.free && (
                      <span className="block text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">GRÁTIS</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center border-l-0 md:border-l border-slate-100 pl-0 md:pl-8">
              <p className="text-sm text-slate-500 line-through mb-1">De R$ 717,90</p>
              <p className="text-sm text-slate-700 mb-2">Por</p>
              <p className="text-5xl font-black text-green-600 mb-2">R$ 147,00</p>
              <p className="text-slate-500 mb-6">Ou 12x de R$ 15,21 *</p>
              <a
                href={CHECKOUT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white text-lg font-black py-4 rounded-xl shadow-xl border-b-4 border-green-800 active:border-b-0 active:translate-y-1 transition-all"
              >
                Desbloquear Desconto
              </a>
              <p className="text-xs text-slate-400 mt-3 flex items-center justify-center gap-2"><Lock size={14} /> Compra 100% Segura e Criptografada</p>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ TURBINADO */}
      <section className="py-16 px-4 bg-white max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-10 text-slate-900">Perguntas Frequentes</h2>
        <div className="space-y-3">
          {[
            { q: "Em quanto tempo vejo resultados?", a: "A maioria dos alunos relata melhora significativa do hálito já nos primeiros 3 a 7 dias aplicando o protocolo de limpeza e ajuste alimentar. O método foi desenhado para ação rápida." },
            { q: "Tenho gastrite, refluxo ou H. Pylori, funciona pra mim?", a: "Sim! O erro comum é achar que o hálito vem só do estômago. Na verdade, 90% vem da boca e da 'saburra' que se forma por causa da acidez. O método ensina a neutralizar essa acidez, independente se você tem refluxo ou não." },
            { q: "Como vou receber o acesso?", a: "Imediatamente após a confirmação do pagamento, você recebe um e-mail automático da Hotmart com seu login e senha para acessar a área de membros. Se for no cartão ou Pix, é na hora." },
            { q: "Tenho cáseos (bolinhas brancas), o método resolve?", a: "Com certeza. Tem um capítulo inteiramente dedicado a eliminar e PREVENIR a formação de cáseos amigdalianos sem precisar de cirurgia ou ficar cutucando a garganta." },
            { q: "E se eu não gostar ou não funcionar?", a: "Você tem 7 dias de garantia incondicional. Teste o método. Se você não sentir seu hálito mais fresco, nós devolvemos 100% do seu dinheiro. O risco é todo nosso." },
            { q: "É seguro colocar meu cartão no site?", a: "Totalmente. O pagamento é processado pela Hotmart, a maior e mais segura plataforma de produtos digitais da América Latina, com criptografia de ponta." },
            { q: "Preciso comprar remédios caros?", a: "O método utiliza ingredientes naturais e ajustes na alimentação que você encontra em qualquer mercado ou farmácia comum. Nada de fórmulas caras." },
            { q: "Funciona para quem usa aparelho ou prótese?", a: "Sim. O método ensina técnicas específicas de higiene para quem usa aparelhos, garantindo que não haja acúmulo de bactérias nas peças." }
          ].map((item, i) => (
            <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
              <button onClick={() => toggleFaq(i)} className="w-full text-left flex justify-between items-center p-5 font-bold text-slate-800 hover:bg-slate-50 transition-colors">
                <span className="flex items-center gap-3"><HelpCircle size={18} className="text-blue-500" /> {item.q}</span>
                <span className="text-slate-400">{faqOpen === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</span>
              </button>
              {faqOpen === i && <div className="p-5 bg-slate-50 text-slate-600 text-sm leading-relaxed border-t border-slate-100">{item.a}</div>}
            </div>
          ))}
        </div>
      </section>

      <footer className="bg-slate-900 py-10 text-center text-slate-500 text-sm space-y-2">
        <p>Hálito Blindado &copy; 2025 - Todos os direitos reservados.</p>
        <p className="flex flex-wrap justify-center gap-4 text-xs">
          <a className="text-blue-300 hover:text-blue-200" href="https://termos.halitoblindado.com.br" target="_blank" rel="noreferrer">Termos de Uso</a>
          <a className="text-blue-300 hover:text-blue-200" href="https://politicas.halitoblindado.com.br" target="_blank" rel="noreferrer">Política de Privacidade</a>
        </p>
        <p className="text-[11px] max-w-3xl mx-auto px-4">
          Mesmo com evidências e alta taxa de satisfação, os resultados podem variar de pessoa para pessoa e não substituem avaliação ou acompanhamento com profissional.
        </p>
      </footer>
    </div>
  );
};

export default App;
